import { ComponentProps, forwardRef } from "react";
import { styled } from "~/application/theme";
import { Icon } from "../Icon/Icon";

export type IconButtonProps = ComponentProps<typeof IconButtonRoot> & {
  boxShadowOnFocus?: boolean;
};

const IconButtonRoot = styled("button", {
  // Reset
  all: "unset",
  boxSizing: "border-box",
  display: "inline-flex",
  userSelect: "none",
  whiteSpace: "nowrap",

  // Custom
  alignItems: "center",
  appearance: "none",
  borderWidth: "0",
  flexShrink: 0,
  fontFamily: "inherit",
  fontSize: "14px",
  justifyContent: "center",
  lineHeight: "1",
  outline: "none",
  padding: "0",
  textDecoration: "none",
  border: 0,
  borderRadius: "$md",
  transition: "$normal",

  "&:not(:disabled):hover": {
    cursor: "pointer",
  },

  "&:disabled": {
    cursor: "not-allowed",
  },

  "&:focus": {
    boxShadow: (props: { boxShadowOnFocus?: boolean }) => {
      return props.boxShadowOnFocus ? "$focus-sm" : "none";
    },
  },

  variants: {
    size: {
      xs: {
        size: "$4",

        [`& ${Icon}`]: {
          size: "$4",
        },
      },
      sm: {
        size: "$6",

        [`& ${Icon}`]: {
          size: "$4",
        },
      },
      md: {
        size: "$8",

        [`& ${Icon}`]: {
          size: "$4",
        },
      },
      lg: {
        size: "$11",

        [`& ${Icon}`]: {
          size: "$5",
        },

        "@mxlg": {
          size: "$10",
        },
      },
      xl: {
        size: "$12",

        [`& ${Icon}`]: {
          size: "$5",
        },

        "@mxlg": {
          size: "$10",
        },
      },
      "2xl": {
        size: "$14",

        [`& ${Icon}`]: {
          size: "$10",
        },
      },
      "4xl": {
        size: "$20",

        [`& ${Icon}`]: {
          size: "$12",
        },
      },
      smlg: {
        size: "$6",
        width: "390px",

        [`& ${Icon}`]: {
          size: "$4",
        },
      },
    },
    variant: {
      "primary-light": {
        backgroundColor: "$neutrals-white",
        color: "$primary-base",
        fill: "$primary-base",
        border: "2px solid $primary-base",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-base",
          color: "$neutrals-white",
          fill: "$neutrals-white",
        },

        "&:disabled": {
          backgroundColor: "$neutrals-light",
          color: "$neutrals-medium",
          fill: "$neutrals-medium",
          borderColor: "$neutrals-light",
        },
      },
      primary: {
        backgroundColor: "$primary-600",
        color: "$neutrals-white",
        fill: "$neutrals-white",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-700",
        },

        "&:disabled": {
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
      "primary-700": {
        backgroundColor: "$primary-700",
        color: "$neutrals-white",
        fill: "$neutrals-white",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-800",
        },

        "&:disabled": {
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
      "primary-800": {
        backgroundColor: "$primary-800",
        color: "$neutrals-white",
        fill: "$neutrals-white",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-900",
        },

        "&:disabled": {
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
      "outline-primary-700": {
        backgroundColor: "transparent",
        color: "$primary-700",
        fill: "$primary-700",
        border: "2px solid $primary-700",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-200",
        },

        "&:disabled": {
          backgroundColor: "$primary-200",
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
      secondary: {
        backgroundColor: "transparent",

        "&:not(:disabled):hover": {
          backgroundColor: "$neutrals-lightest",
        },

        "&:disabled": {
          backgroundColor: "transparent",
          color: "$neutrals-medium",
          fill: "$neutrals-medium",
          opacity: 0.5,
        },
      },
      "outline-secondary": {
        backgroundColor: "transparent",
        color: "$neutral-600",
        fill: "$neutral-600",
        border: "2px solid $neutral-600",

        "&:not(:disabled):hover": {
          backgroundColor: "$neutral-100",
        },

        "&:disabled": {
          backgroundColor: "$neutral-100",
          opacity: 0.5,
        },
      },
      tertiary: {
        backgroundColor: "$neutral-300",
        color: "$neutral-500",
        fill: "$neutral-500",

        "&:not(:disabled):hover": {
          backgroundColor: "$neutral-200",
        },

        "&:disabled": {
          backgroundColor: "$neutral-200",
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
      success: {
        backgroundColor: "$success-base",
        color: "$neutrals-white",
        fill: "$neutrals-white",

        "&:not(:disabled):hover": {
          backgroundColor: "$success-dark",
        },

        "&:disabled": {
          backgroundColor: "$neutrals-light",
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
      "outline-primary": {
        backgroundColor: "transparent",
        color: "$primary-600",
        fill: "$primary-600",
        border: "2px solid $primary-600",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-100",
        },

        "&:disabled": {
          backgroundColor: "$primary-100",
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
      error: {
        backgroundColor: "$error-base",
        color: "$neutrals-white",
        fill: "$neutrals-white",

        "&:not(:disabled):hover": {
          backgroundColor: "$error-dark",
        },

        "&:disabled": {
          backgroundColor: "$neutrals-light",
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
      "outline-danger": {
        backgroundColor: "transparent",
        color: "$danger-600",
        fill: "$danger-600",
        border: "2px solid $danger-600",

        "&:not(:disabled):hover": {
          backgroundColor: "$danger-100",
        },

        "&:disabled": {
          backgroundColor: "$danger-100",
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
      "outline-danger-300": {
        backgroundColor: "transparent",
        color: "$danger-300",
        fill: "$danger-300",
        border: "2px solid $danger-300",

        "&:not(:disabled):hover": {
          backgroundColor: "$danger-100",
        },

        "&:disabled": {
          backgroundColor: "$danger-100",
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
      ghost: {
        backgroundColor: "transparent",
        color: "$primary-600",
        fill: "$primary-600",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-100",
        },

        "&:disabled": {
          backgroundColor: "transparent",
          color: "$primary-200",
          fill: "$primary-200",
        },
      },
      "ghost-danger": {
        backgroundColor: "transparent",
        color: "$danger-600",
        fill: "$danger-600",

        "&:not(:disabled):hover": {
          backgroundColor: "$danger-50",
        },

        "&:disabled": {
          backgroundColor: "transparent",
          color: "$neutral-200",
          fill: "$neutral-200",
        },
      },
      "outline-neutral": {
        backgroundColor: "transparent",
        color: "$neutral-500",
        fill: "$neutral-500",
        border: "2px solid $neutral-500",

        "&:not(:disabled):hover": {
          backgroundColor: "$neutral-100",
        },

        "&:disabled": {
          backgroundColor: "$neutral-100",
          cursor: "not-allowed",
          opacity: 0.5,
        },
      },
    },
  },

  defaultVariants: {
    size: "sm",
    variant: "secondary",
  },
});

export const IconButton = forwardRef<React.ElementRef<typeof IconButtonRoot>, IconButtonProps>(
  (props, forwardedRef) => {
    return <IconButtonRoot type="button" {...props} ref={forwardedRef} />;
  },
);

IconButton.displayName = "IconButton";
